import { useState, useCallback } from 'react';
import customerWarehousesService from '../services/customerWarehousesService';
import locationsService from '../services/locationsService';

/**
 * Turns the customer's own address into a real delivery location.
 *
 * The order/inquiry/PO forms offer "Use customer's address" as an option. Picking it
 * calls this, which goes through the customer-warehouse flow — the address becomes a
 * saved delivery site for that customer, the backend resolves-or-creates the underlying
 * `locations` row, and the form gets back a real numeric location id.
 *
 * Resolution is EAGER, at selection time, not at submit. Every form posts
 * `Number(location_id) || 0`, so any placeholder left sitting in `location_id` would be
 * silently submitted as `0` — the bug this whole path exists to avoid. By the time this
 * resolves, the form only ever holds a real id.
 *
 * The cost is a warehouse row created even if the order is later abandoned. The backend
 * de-duplicates the location, and the option disappears for that customer once it exists,
 * so it happens at most once per address.
 */
const useCustomerDeliveryLocation = () => {
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  /**
   * @param {object} args
   * @param {'existing'|'new'} args.mode  'new' = the customer row does not exist yet
   * @param {number} [args.customerId]    required when mode === 'existing'
   * @param {object} args.draft           from buildCustomerAddressDraft
   * @param {string} args.warehouseName   label for the saved site
   * @returns {Promise<{success, locationId?, location?, warehouse?, error?}>}
   */
  const resolveCustomerAddress = useCallback(
    async ({ mode = 'existing', customerId, draft, warehouseName }) => {
      if (!draft || !draft.materializable) {
        return { success: false, error: 'Customer address is incomplete' };
      }

      setResolving(true);
      setResolveError(null);

      const locationPayload = {
        delivery_location: draft.delivery_location,
        city: draft.city,
        state: draft.state,
        country: draft.country || 'India',
        is_active: true,
      };
      const name = warehouseName || draft.city || draft.delivery_location;

      try {
        // No customer row yet: create the location only and hand back a warehouse draft
        // for the caller to carry in the form's existing `warehouses[]` payload, which
        // is how new-customer warehouses already reach the server.
        if (mode === 'new' || !customerId) {
          const res = await locationsService.createLocation(locationPayload);
          if (!res.success || !res.data?.id) {
            const error = res.error || 'Could not create the delivery location';
            setResolveError(error);
            return { success: false, error };
          }
          return {
            success: true,
            locationId: res.data.id,
            location: res.data,
            warehouse: {
              name,
              location_id: res.data.id,
              country: draft.country || 'India',
              state: draft.state,
              city: draft.city,
              address: draft.delivery_location,
              map_url: '',
              contact_person: '',
              contact_phone: '',
              is_default: false,
            },
          };
        }

        // Existing customer: one call. The backend resolves-or-creates the location and
        // links it, so the address shows as a saved site from here on.
        const res = await customerWarehousesService.createWarehouse(customerId, {
          name,
          location_id: null,
          country: draft.country || 'India',
          state: draft.state,
          city: draft.city,
          address: draft.delivery_location,
          map_url: '',
          contact_person: '',
          contact_phone: '',
          is_default: false,
        });

        if (res.success && res.data?.location_id) {
          return {
            success: true,
            locationId: res.data.location_id,
            location: res.data.location || { id: res.data.location_id, ...locationPayload },
            warehouse: res.data,
          };
        }

        const error = res.error || 'Could not save the address as a delivery site';
        setResolveError(error);
        return { success: false, error };
      } catch (err) {
        const error = err?.message || 'Could not save the address as a delivery site';
        setResolveError(error);
        return { success: false, error };
      } finally {
        setResolving(false);
      }
    },
    []
  );

  return { resolving, resolveError, setResolveError, resolveCustomerAddress };
};

export default useCustomerDeliveryLocation;
